"use client";
import React from "react";
import type {PageAffiliateThankYouSchema} from "@/sanity/pages/PageAffiliateThankYouTemplate";

import Image from "next/image";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import NavBarV2 from "@/components/global/partials/HeaderV2";
import Footer from "../global/partials/Footer";
import {CTAButton} from "@/shadcn/ui/button";
import Countdown from "@/components/global/countdown/Countdown";

import logo from "@/public/global/iqcapital.svg";
import bg from "@/public/global/bg-art.jpg";
import christoph from "@/public/global/christoph.png";

export default function PageAffiliateThankYouJSX({
  data,
}: {
  data: PageAffiliateThankYouSchema;
}) {
  return (
    <>
      {data?.settings.header && (
        <NavBarV2 headerData={data?.settings.header}/>
      )}

      <section className="relative overflow-clip sm:p-[2rem] lg:p-[5rem]">
        <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[2.5rem] lg:pt-[5.5rem] pb-[12rem] lg:pb-[17rem]">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB] pointer-events-none">
            <Image
              className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-10"
              src={bg}
              quality={100}
              alt="background art"
            />
          </div>

          <div className="container lg:w-[min(182.5rem,100%)] px-[3rem] relative z-10">
            <div className="flex flex-col items-center md:flex-row md:justify-between gap-[2rem]">
              <div className="flex items-center gap-[2rem] lg:gap-[3rem]">
                <Image
                  className="!w-[15rem]"
                  src={logo}
                  alt="logo"
                />

                {data?.logoText && (
                  <>
                    <svg className="!w-[3rem]"
                         viewBox="0 0 19 19"
                         fill="none"
                         xmlns="http://www.w3.org/2000/svg">
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 2.89819 15.9276)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 2.89819 4.34407)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 5.79034 13.0316)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 5.79034 7.23958)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 5.79034 15.9276)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 5.79034 4.34407)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 8.68866 13.0316)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 8.68866 7.23958)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 8.68866 10.1356)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 8.68866 15.9276)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 8.68866 4.34407)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 11.5808 13.0316)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 11.5808 7.23958)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 11.5808 10.1356)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                      <rect width="1.44795"
                            height="1.44795"
                            rx="0.289591"
                            transform="matrix(1 0 0 -1 14.4791 10.1356)"
                            fill="#6DA991"
                            fillOpacity="0.5"/>
                    </svg>

                    <p className="mb-0 leading-none">{data.logoText}</p>
                  </>
                )}
              </div>

              <div className="flex">
                {data?.date1 && (
                  <p className="mb-0 leading-none text-[1.75rem] font-medium text-[#103037]/50">
                    {new Date(data.date1).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}

                    {data?.date2 && (
                      <span className="inline-block mx-[.5rem]">/</span>
                    )}
                  </p>
                )}

                {data?.date2 && (
                  <p className="mb-0 leading-none text-[1.75rem] font-medium text-[#103037]">
                    {new Date(data.date2).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col items-center lg:flex-row lg:justify-between gap-[3rem] mt-[5rem] lg:mt-[11.5rem]">
              <div className="w-[min(78.375rem,100%)] flex flex-col items-start">
                {data?.heroText && (
                  <div className="richtext lg:richtext-start prose-strong:font-medium prose-strong:text-[#103037] prose-p:text-[#103037]/90 prose-p:text-[2.25rem] prose-p:mb-[1rem] prose-p:[text-wrap:balance] mb-[2.25rem]">
                    <PortableText
                      value={data.heroText}
                      components={richTextComponent}
                    />
                  </div>
                )}

                <div className="w-[min(77.75rem,100%)] flex lg:hidden flex-col gap-[1.5rem] mb-[3rem]">
                  <div className="grid grid-cols-2 gap-[1.5rem] lg:gap-[2.25rem]">
                    <div className="aspect-[302/482] w-full rounded-[.75rem] lg:rounded-[1.5rem] overflow-clip relative">
                      <Image
                        className="!w-full !h-full object-cover object-bottom"
                        src={data?.person1?.image ?? christoph}
                        quality={90}
                        alt=""
                      />

                      <p className="absolute mb-0 left-[.75rem] top-[.75rem] lg:left-[1.5rem] lg:top-[1.5rem] bg-white/10 backdrop-blur-[2rem] rounded-[.5rem] lg:rounded-[1rem] min-w-[12rem] lg:min-w-[14rem] min-h-[3.25rem] lg:min-h-[4.25rem] px-[1.5rem] flex items-center justify-center text-white font-gestura italic text-[1.75rem] lg:text-[2.25rem] tracking-tight">
                        {data?.person1?.name ?? "Christoph"}
                      </p>
                    </div>

                    <div className="aspect-[302/482] w-full rounded-[.75rem] lg:rounded-[1.5rem] overflow-clip relative">
                      {data?.person2?.image && (
                        <Image
                          className="!w-full !h-full object-cover object-bottom"
                          src={data.person2.image}
                          quality={90}
                          alt=""
                        />
                      )}

                      {data?.person2?.name && (
                        <p className="absolute mb-0 left-[.75rem] top-[.75rem] lg:left-[1.5rem] lg:top-[1.5rem] bg-white/10 backdrop-blur-[2rem] rounded-[.5rem] lg:rounded-[1rem] min-w-[12rem] lg:min-w-[14rem] min-h-[3.25rem] lg:min-h-[4.25rem] px-[1.5rem] flex items-center justify-center text-white font-gestura italic text-[1.75rem] lg:text-[2.25rem] tracking-tight">
                          {data.person2.name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="bg-[#EBF4EF] border border-[#103037]/10 rounded-[1.5rem] lg:rounded-[2.25rem] prose-h2:text-[#103037] prose-h6:text-[#103037]/70 lg:[&_.countdownUnitItem]:py-[4.5rem]">
                    <Countdown
                      dateTimeString={data?.countdown}
                      lang={data?.countdownTextLang ?? "de"}
                    />
                  </div>
                </div>

                {data?.heroItems && (
                  <div className="flex flex-col gap-[2.25rem]">
                    {data?.heroItems.map((item, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-[2rem] lg:gap-[2.5rem] bg-white rounded-[1.25rem] lg:rounded-[2rem] p-[3rem] lg:py-[4rem] lg:px-[3.5rem]"
                      >
                        <div className="flex-shrink-0 w-[5.5rem] aspect-square rounded-[1rem] bg-[#103037] flex items-center justify-center text-white text-[2.5rem] font-medium">
                          {i + 1}
                        </div>

                        {item?.text && (
                          <div className="richtext richtext-start prose-strong:font-medium prose-strong:text-[#103037] prose-p:text-[#103037]/70 prose-p:mb-[1rem]">
                            <PortableText
                              value={item.text}
                              components={richTextComponent}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {data?.heroCTA && (
                  <CTAButton
                    className="mt-[4rem] sm:min-w-[35rem]"
                    data={data.heroCTA}
                  />
                )}
              </div>

              <div className="w-[min(77.75rem,100%)] hidden lg:flex flex-col gap-[1.5rem]">
                <div className="grid grid-cols-2 gap-[1.5rem] lg:gap-[2.25rem]">
                  <div className="aspect-[302/482] w-full rounded-[.75rem] lg:rounded-[1.5rem] overflow-clip relative">
                    <Image
                      className="!w-full !h-full object-cover object-bottom"
                      src={data?.person1?.image ?? christoph}
                      quality={90}
                      alt=""
                    />

                    <p className="absolute mb-0 left-[.75rem] top-[.75rem] lg:left-[1.5rem] lg:top-[1.5rem] bg-white/10 backdrop-blur-[2rem] rounded-[.5rem] lg:rounded-[1rem] min-w-[12rem] lg:min-w-[14rem] min-h-[3.25rem] lg:min-h-[4.25rem] px-[1.5rem] flex items-center justify-center text-white font-gestura italic text-[1.75rem] lg:text-[2.25rem] tracking-tight">
                      {data?.person1?.name ?? "Christoph"}
                    </p>
                  </div>

                  <div className="aspect-[302/482] w-full rounded-[.75rem] lg:rounded-[1.5rem] overflow-clip relative">
                    {data?.person2?.image && (
                      <Image
                        className="!w-full !h-full object-cover object-bottom"
                        src={data.person2.image}
                        quality={90}
                        alt=""
                      />
                    )}

                    {data?.person2?.name && (
                      <p className="absolute mb-0 left-[.75rem] top-[.75rem] lg:left-[1.5rem] lg:top-[1.5rem] bg-white/10 backdrop-blur-[2rem] rounded-[.5rem] lg:rounded-[1rem] min-w-[12rem] lg:min-w-[14rem] min-h-[3.25rem] lg:min-h-[4.25rem] px-[1.5rem] flex items-center justify-center text-white font-gestura italic text-[1.75rem] lg:text-[2.25rem] tracking-tight">
                        {data.person2.name}
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-[#EBF4EF] border border-[#103037]/10 rounded-[1.5rem] lg:rounded-[2.25rem] prose-h2:text-[#103037] prose-h6:text-[#103037]/70 lg:[&_.countdownUnitItem]:py-[4.5rem]">
                  <Countdown
                    dateTimeString={data?.countdown}
                    lang={data?.countdownTextLang ?? "de"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {data?.settings?.footer && (
        <Footer data={data.settings.footer} slug={data?.settings?.slug?.current}/>
      )}
    </>
  );
}
